import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../api/client';
import { personalTaskTypes } from '../utils/constants';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import EventProposalsBanner from './EventProposalsBanner';
import { useLearntDefaults } from '../hooks/useLearntDefaults';

const MEMBER_COLOURS = [
  '#3b82f6','#10b981','#f59e0b','#8b5cf6','#ef4444','#06b6d4','#f97316','#84cc16',
];

function memberColour(id) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return MEMBER_COLOURS[h % MEMBER_COLOURS.length];
}

function isoDate(d) { return d.toISOString().slice(0, 10); }

function weekMonday(ref) {
  const d = new Date(ref); d.setHours(0,0,0,0);
  const day = d.getDay(); d.setDate(d.getDate() + (day === 0 ? -6 : 1 - day));
  return d;
}

function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }

function fmtDay(d) { return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }); }
function fmtDayFull(d) { return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' }); }
function fmtWeekRange(mon) {
  return mon.toLocaleDateString('en-GB',{day:'numeric',month:'short'}) + ' – ' +
    addDays(mon,6).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'});
}

function getSlot(timeStr) {
  if (!timeStr) return 'allday';
  const [h] = timeStr.split(':').map(Number);
  if (h < 12) return 'morning';
  if (h < 18) return 'afternoon';
  return 'evening';
}

const SLOT_LABEL = { allday: 'All day', morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening' };

const BLANK_FORM = { title:'', date:'', time:'', endTime:'', taskType:'Task', priority:'Medium', householdMemberId:'', visibility:'shared', recurrenceEnabled: false, recurrenceFrequency:'weekly', recurrenceDay:'monday', details:'' };

const inputStyle = { width:'100%', padding:'8px 10px', borderRadius:8, border:'1px solid #d1d5db', fontSize:14, boxSizing:'border-box', outline:'none' };
const labelStyle = { display:'block', fontSize:12, fontWeight:600, color:'#555', marginBottom:3 };
const fieldStyle = { marginBottom:12 };

const DAY_NAMES = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'];

function monthStart(d) {
  const r = new Date(d); r.setDate(1); r.setHours(0,0,0,0); return r;
}

export default function FamilyCalendar({ googleEvents = [], icalEvents = [], outlookEvents = [] }) {
  const { currentUser } = useCurrentUser();
  const visibleTaskTypes = currentUser?.isChildAccount
    ? personalTaskTypes.filter(t => t.value !== 'Date Night')
    : personalTaskTypes;
  const [monday, setMonday]       = useState(() => weekMonday(new Date()));
  const [viewMode, setViewMode]   = useState('week');      // 'week' | 'day' | 'month'
  const [dayIndex, setDayIndex]   = useState(0);           // 0-6, used in day view
  const [monthAnchor, setMonthAnchor] = useState(() => monthStart(new Date()));
  const [tasks, setTasks]         = useState([]);
  const [members, setMembers]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [catFilter, setCatFilter] = useState('');          // '' = all
  const [memberFilter, setMemberFilter] = useState('');    // '' = all

  const [modal, setModal]         = useState(null);        // null | 'add' | 'edit'
  const [editTask, setEditTask]   = useState(null);
  const [form, setForm]           = useState(BLANK_FORM);
  const learnt = useLearntDefaults();
  const [saving, setSaving]       = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const isMobile = window.innerWidth < 640;

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const [tRes, mRes] = await Promise.all([api('/api/tasks'), api('/api/household')]);
      if (!tRes.ok) throw new Error('tasks');
      const { tasks: raw } = await tRes.json();
      setTasks(raw || []);
      if (mRes.ok) setMembers((await mRes.json()).members || []);
    } catch { setError('Could not load calendar.'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── All events for the displayed week ──────────────────────────────────────
  const days = Array.from({ length: 7 }, (_, i) => addDays(monday, i));

  const allEvents = [
    ...tasks.map(t => ({ ...t, _kind: 'task', _date: t.date || (t.datetime ? t.datetime.slice(0,10) : null) })),
    ...[...googleEvents, ...icalEvents, ...outlookEvents].map(e => ({
      id: e.id || e.title, title: e.title || e.summary || e.subject || 'Event',
      _kind: 'external', _date: (e.start?.dateTime || e.start?.date || e.datetime || '').slice(0,10),
      time: (e.start?.dateTime || e.datetime || '').slice(11,16) || null,
      taskType: 'Event',
    })),
  ];

  function eventsForDay(dateStr) {
    return allEvents.filter(e => {
      if (e._date !== dateStr) return false;
      if (catFilter && e.taskType !== catFilter) return false;
      if (memberFilter && e._kind === 'task' && e.householdMemberId !== memberFilter) return false;
      return true;
    });
  }

  // ── Add / Edit ──────────────────────────────────────────────────────────────
  function openAdd(dateStr) {
    // Carla, 2026-10-12: a new task starts on her usual priority (Aidey Intelligence), as the Add task form does.
    setForm({ ...BLANK_FORM, date: dateStr, ...(learnt?.taskPriority?.priority ? { priority: learnt.taskPriority.priority } : {}) });
    setEditTask(null); setDeleteConfirm(false); setModal('add');
  }

  function openEdit(task) {
    setEditTask(task);
    setDeleteConfirm(false);
    setForm({
      title: task.title || '',
      date: task.date || task.datetime?.slice(0,10) || '',
      time: task.time || '',
      endTime: task.endTime || '',
      taskType: task.taskType || 'Task',
      priority: task.priority || 'Medium',
      householdMemberId: task.householdMemberId || '',
      visibility: task.visibility || 'shared',
      recurrenceEnabled: !!(task.recurrence),
      recurrenceFrequency: task.recurrence?.type || 'weekly',
      recurrenceDay: task.recurrence?.day || 'monday',
      details: task.details || '',
    });
    setModal('edit');
  }

  function closeModal() { setModal(null); setEditTask(null); setDeleteConfirm(false); }

  function buildRecurrence() {
    if (!form.recurrenceEnabled) return undefined;
    const r = { type: form.recurrenceFrequency };
    if (form.recurrenceFrequency === 'weekly') r.day = form.recurrenceDay;
    return r;
  }

  async function saveTask() {
    if (!form.title.trim() || !form.date) return;
    setSaving(true);
    try {
      const body = {
        id: editTask ? String(editTask.id) : String(Date.now()),
        title: form.title.trim(),
        date: form.date,
        time: form.time || null,
        endTime: form.endTime || null,
        taskType: form.taskType || null,
        priority: form.priority,
        householdMemberId: form.householdMemberId || null,
        visibility: form.visibility,
        recurrence: buildRecurrence(),
        details: form.details || null,
      };
      const res = editTask
        ? await api(`/api/tasks/${editTask.id}`, { method:'PUT', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ ...body, dueChange: { source: 'task_edit' } }) })
        : await api('/api/tasks', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify(body) });
      if (!res.ok) throw new Error('save failed');
      await load(); closeModal();
    } catch { setError('Could not save task.'); }
    finally { setSaving(false); }
  }

  async function deleteTask() {
    if (!editTask) return;
    try {
      await api(`/api/tasks/${editTask.id}`, { method: 'DELETE' });
      await load(); closeModal();
    } catch { setError('Could not delete task.'); }
  }

  async function toggleComplete(task) {
    try {
      await api(`/api/tasks/${task.id}`, {
        method: 'PUT',
        headers: {'Content-Type':'application/json'},
        body: JSON.stringify({ completed: !task.completed }),
      });
      await load();
    } catch { setError('Could not update task.'); }
  }

  // ── Member chip ─────────────────────────────────────────────────────────────
  function MemberChip({ memberId, size = 18 }) {
    const m = members.find(x => x.id === memberId);
    if (!m) return null;
    const initials = m.name.split(' ').map(n => n[0]).join('').slice(0,2).toUpperCase();
    return (
      <span style={{
        display:'inline-flex', alignItems:'center', justifyContent:'center',
        width: size, height: size, borderRadius:'50%',
        background: memberColour(m.id), color:'#fff',
        fontSize: size * 0.45, fontWeight:700,
        flexShrink:0, title: m.name,
      }} title={m.name}>{initials}</span>
    );
  }

  // ── Task pill ───────────────────────────────────────────────────────────────
  function TaskPill({ event, compact }) {
    const isExternal = event._kind === 'external';
    const isPrivate  = event.visibility === 'private';
    const isDone     = event.completed;
    const typeEntry  = personalTaskTypes.find(t => t.value === event.taskType);
    return (
      <div
        onClick={() => !isExternal && openEdit(event)}
        style={{
          display:'flex', alignItems:'center', gap:4,
          padding: compact ? '3px 6px' : '4px 8px',
          borderRadius:6, marginBottom:2, cursor: isExternal ? 'default' : 'pointer',
          background: isDone ? '#f3f4f6' : isExternal ? '#eff6ff' : '#f0fdf4',
          border: `1px solid ${isDone ? '#e5e7eb' : isExternal ? '#bfdbfe' : '#bbf7d0'}`,
          opacity: isDone ? 0.6 : 1,
        }}
      >
        {!isExternal && (
          <input type="checkbox" checked={isDone} style={{flexShrink:0, cursor:'pointer'}}
            onClick={e => { e.stopPropagation(); toggleComplete(event); }} onChange={()=>{}}/>
        )}
        <span style={{ fontSize:11, flex:1, fontWeight:600, color: isDone ? '#9ca3af' : '#166534', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {typeEntry ? typeEntry.label.slice(0,2) : (isExternal ? '📅' : '📋')} {event.title}
        </span>
        {isPrivate && <span title="Private" style={{fontSize:10}}>🔒</span>}
        {event.householdMemberId && <MemberChip memberId={event.householdMemberId} size={16} />}
      </div>
    );
  }

  // ── Day column (week view) ──────────────────────────────────────────────────
  function DayColumn({ day }) {
    const dateStr = isoDate(day);
    const isToday = dateStr === isoDate(new Date());
    const evs = eventsForDay(dateStr);
    return (
      <div style={{ flex:1, minWidth:0, display:'flex', flexDirection:'column' }}>
        <div
          onClick={() => { setDayIndex(days.indexOf(day)); setViewMode('day'); }}
          style={{ cursor:'pointer', textAlign:'center', padding:'6px 2px', fontWeight:700, fontSize:11,
            color: isToday ? '#1d4ed8' : '#374151',
            background: isToday ? '#eff6ff' : '#f9fafb',
            borderRadius:'6px 6px 0 0', borderBottom:'1px solid #e5e7eb' }}
        >
          {fmtDay(day)}{isToday ? <div style={{fontSize:9,color:'#3b82f6'}}>Today</div> : null}
        </div>
        <div
          style={{ flex:1, minHeight:80, padding:'4px 2px', cursor:'text' }}
          onClick={() => openAdd(dateStr)}
        >
          {evs.map(e => <TaskPill key={e.id || e._date + e.title} event={e} compact />)}
          {evs.length === 0 && <span style={{fontSize:11,color:'#d1d5db',display:'block',textAlign:'center',marginTop:8}}>+</span>}
        </div>
      </div>
    );
  }

  // ── Day view ─────────────────────────────────────────────────────────────────
  function DayView() {
    const day = days[dayIndex];
    const dateStr = isoDate(day);
    const evs = eventsForDay(dateStr);
    const grouped = { allday:[], morning:[], afternoon:[], evening:[] };
    evs.forEach(e => grouped[getSlot(e.time)].push(e));

    return (
      <div>
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
          <button onClick={() => setDayIndex(i => Math.max(0, i-1))} style={{padding:'6px 10px',borderRadius:8,border:'1px solid #d1d5db',background:'#fff',cursor:'pointer'}}>‹</button>
          <span style={{flex:1,textAlign:'center',fontWeight:700,fontSize:15,color:'#1a2e3b'}}>{fmtDayFull(day)}</span>
          <button onClick={() => setDayIndex(i => Math.min(6, i+1))} style={{padding:'6px 10px',borderRadius:8,border:'1px solid #d1d5db',background:'#fff',cursor:'pointer'}}>›</button>
        </div>
        {(['allday','morning','afternoon','evening']).map(slot => {
          const items = grouped[slot];
          return (
            <div key={slot} style={{marginBottom:16}}>
              <div style={{fontSize:11,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.05em',color:'#9ca3af',marginBottom:4}}>{SLOT_LABEL[slot]}</div>
              {items.map(e => <TaskPill key={e.id || e._date+e.title} event={e} />)}
              <div
                onClick={() => openAdd(dateStr)}
                style={{fontSize:12,color:'#9ca3af',padding:'4px 8px',borderRadius:6,border:'1px dashed #d1d5db',cursor:'pointer',marginTop:4,textAlign:'center'}}
              >+ Add</div>
            </div>
          );
        })}
      </div>
    );
  }

  // ── Modal ────────────────────────────────────────────────────────────────────
  function TaskModal() {
    return (
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.35)',zIndex:'var(--z-modal)',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}
        onClick={e => { if (e.target === e.currentTarget) closeModal(); }}>
        <div style={{background:'#fff',borderRadius:16,padding:24,width:'100%',maxWidth:420,boxShadow:'0 20px 60px rgba(0,0,0,0.15)',maxHeight:'90vh',overflowY:'auto'}}>
          <h3 style={{margin:'0 0 16px',fontSize:16,fontWeight:700,color:'#1a2e3b'}}>
            {modal === 'edit' ? 'Edit' : 'Add'} task
          </h3>
          <div style={fieldStyle}>
            <label style={labelStyle}>Title</label>
            <input autoFocus style={inputStyle} value={form.title} onChange={e=>setForm(f=>({...f,title:e.target.value}))}
              placeholder="e.g. Bins, Dentist, Football" onKeyDown={e=>e.key==='Enter'&&saveTask()} />
          </div>
          <div style={{display:'flex',gap:8,...fieldStyle}}>
            <div style={{flex:1}}>
              <label style={labelStyle}>Date</label>
              <input type="date" style={inputStyle} value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))} />
            </div>
            <div style={{flex:1}}>
              <label style={labelStyle}>Time (optional)</label>
              <input type="time" style={inputStyle} value={form.time} onChange={e=>setForm(f=>({...f,time:e.target.value}))} />
            </div>
            <div style={{flex:1}}>
              <label style={labelStyle}>End time (optional)</label>
              <input type="time" style={inputStyle} value={form.endTime} onChange={e=>setForm(f=>({...f,endTime:e.target.value}))} />
            </div>
          </div>
          <div style={{display:'flex',gap:8,...fieldStyle}}>
            <div style={{flex:1}}>
              <label style={labelStyle}>Category</label>
              <select style={{...inputStyle}} value={form.taskType} onChange={e=>setForm(f=>({...f,taskType:e.target.value}))}>
                {visibleTaskTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div style={{flex:1}}>
              <label style={labelStyle}>Priority</label>
              <select style={{...inputStyle}} value={form.priority} onChange={e=>setForm(f=>({...f,priority:e.target.value}))}>
                <option value="High">🟠 High</option>
                <option value="Medium">🟡 Medium</option>
                <option value="Low">🟢 Low</option>
              </select>
              {!editTask && learnt?.taskPriority?.priority === form.priority && (
                <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--ink-2)' }}>
                  {learnt.taskPriority.mode === 'auto' ? 'Your usual priority, set by Aidey.' : "Aidey's suggestion: your usual priority."}
                </p>
              )}
            </div>
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>For (optional)</label>
            {members.length > 0 ? (
              <select style={{...inputStyle}} value={form.householdMemberId} onChange={e=>setForm(f=>({...f,householdMemberId:e.target.value}))}>
                <option value="">Whole household</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            ) : (
              <>
                <select style={{...inputStyle,color:'#9ca3af',cursor:'not-allowed'}} value="" disabled>
                  <option value="">Whole household</option>
                </select>
                <div style={{fontSize:12,color:'#9ca3af',marginTop:4}}>No household members yet, add them in Family to tag tasks.</div>
              </>
            )}
          </div>
          <div style={fieldStyle}>
            <label style={{...labelStyle,cursor:'pointer',display:'flex',alignItems:'center',gap:8}}>
              <input type="checkbox" checked={form.visibility==='private'} onChange={e=>setForm(f=>({...f,visibility:e.target.checked?'private':'shared'}))} />
              Private, only you will see this
            </label>
          </div>
          <div style={fieldStyle}>
            <label style={{...labelStyle,cursor:'pointer',display:'flex',alignItems:'center',gap:8}}>
              <input type="checkbox" checked={form.recurrenceEnabled} onChange={e=>setForm(f=>({...f,recurrenceEnabled:e.target.checked}))} />
              Repeat this task
            </label>
          </div>
          {form.recurrenceEnabled && (
            <div style={{...fieldStyle,paddingLeft:12,borderLeft:'2px solid #e5e7eb'}}>
              <select style={{...inputStyle,marginBottom:8}} value={form.recurrenceFrequency} onChange={e=>setForm(f=>({...f,recurrenceFrequency:e.target.value}))}>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="weekday">Every weekday</option>
                <option value="monthly">Monthly</option>
              </select>
              {form.recurrenceFrequency === 'weekly' && (
                <select style={{...inputStyle}} value={form.recurrenceDay} onChange={e=>setForm(f=>({...f,recurrenceDay:e.target.value}))}>
                  {DAY_NAMES.map(d => <option key={d} value={d}>{d.charAt(0).toUpperCase()+d.slice(1)}</option>)}
                </select>
              )}
            </div>
          )}
          <div style={{...fieldStyle}}>
            <label style={labelStyle}>Notes (optional)</label>
            <textarea style={{...inputStyle,resize:'vertical',minHeight:52}} value={form.details} onChange={e=>setForm(f=>({...f,details:e.target.value}))} />
          </div>
          {modal === 'edit' && !deleteConfirm && (
            <button onClick={()=>setDeleteConfirm(true)}
              style={{display:'block',width:'100%',marginBottom:8,padding:'9px 0',borderRadius:10,border:'1px solid #fca5a5',background:'#fff7f7',color:'#b91c1c',fontWeight:600,fontSize:14,cursor:'pointer'}}>
              Delete task
            </button>
          )}
          {deleteConfirm && (
            <div style={{marginBottom:8}}>
              <p style={{fontSize:13,color:'#b91c1c',margin:'0 0 8px'}}>Delete this task?</p>
              <div style={{display:'flex',gap:8}}>
                <button onClick={()=>setDeleteConfirm(false)} style={{flex:1,padding:'8px 0',borderRadius:8,border:'1px solid #d1d5db',background:'#fff',cursor:'pointer',fontSize:13}}>Cancel</button>
                <button onClick={deleteTask} style={{flex:1,padding:'8px 0',borderRadius:8,border:'none',background:'#ef4444',color:'#fff',fontWeight:700,fontSize:13,cursor:'pointer'}}>Delete</button>
              </div>
            </div>
          )}
          <div style={{display:'flex',gap:8}}>
            <button onClick={closeModal} style={{flex:1,padding:'9px 0',borderRadius:10,border:'1px solid #d1d5db',background:'#fff',color:'#374151',fontWeight:600,fontSize:14,cursor:'pointer'}}>Cancel</button>
            <button onClick={saveTask} disabled={saving||!form.title.trim()||!form.date}
              style={{flex:2,padding:'9px 0',borderRadius:10,border:'none',background:(form.title.trim()&&form.date)?'#1a2e3b':'#e5e7eb',color:(form.title.trim()&&form.date)?'#fff':'#9ca3af',fontWeight:700,fontSize:14,cursor:(form.title.trim()&&form.date)?'pointer':'default'}}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Month view helpers ────────────────────────────────────────────────────────
  function getMonthGrid(anchor) {
    const gridStart = weekMonday(anchor);
    return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  }

  function goToDay(date) {
    const wMon = weekMonday(date);
    setMonday(wMon);
    setDayIndex(Math.round((date - wMon) / 86400000));
    setViewMode('day');
  }

  function MonthView() {
    const grid = getMonthGrid(monthAnchor);
    const todayStr = isoDate(new Date());
    const thisMonth = monthAnchor.getMonth();
    const thisYear  = monthAnchor.getFullYear();
    const DOW = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
    return (
      <div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:1,background:'#e5e7eb',border:'1px solid #e5e7eb',borderRadius:10,overflow:'hidden'}}>
          {DOW.map(d => (
            <div key={d} style={{background:'#f9fafb',padding:'6px 0',textAlign:'center',fontSize:11,fontWeight:700,color:'#6b7280',textTransform:'uppercase',letterSpacing:'0.05em'}}>{d}</div>
          ))}
          {grid.map((day, i) => {
            const ds = isoDate(day);
            const isToday   = ds === todayStr;
            const inMonth   = day.getMonth() === thisMonth && day.getFullYear() === thisYear;
            const evs = eventsForDay(ds);
            const visible = evs.slice(0, 3);
            const extra   = evs.length - 3;
            return (
              <div
                key={ds}
                onClick={() => goToDay(day)}
                style={{
                  background: isToday ? '#eff6ff' : '#fff',
                  minHeight: isMobile ? 64 : 80,
                  padding: '4px 3px',
                  cursor: 'pointer',
                  borderTop: isToday ? '2px solid #3b82f6' : '2px solid transparent',
                }}
              >
                <div style={{
                  fontWeight: isToday ? 800 : 600,
                  fontSize: 12,
                  color: !inMonth ? '#c9cdd4' : isToday ? '#1d4ed8' : '#1a2e3b',
                  marginBottom: 2,
                }}>
                  {isToday ? (
                    <span style={{background:'#3b82f6',color:'#fff',borderRadius:'50%',width:18,height:18,display:'inline-flex',alignItems:'center',justifyContent:'center',fontSize:11}}>
                      {day.getDate()}
                    </span>
                  ) : day.getDate()}
                </div>
                {inMonth && visible.map(e => {
                  const typeEntry = personalTaskTypes.find(t => t.value === e.taskType);
                  const emoji = typeEntry ? typeEntry.label.slice(0,2) : (e._kind==='external' ? '📅' : '📋');
                  return (
                    <div key={e.id || ds+e.title} style={{
                      fontSize: 10, fontWeight: 600,
                      color: e.completed ? '#9ca3af' : '#1a2e3b',
                      background: e._kind==='external' ? '#eff6ff' : 'rgba(228,230,155,0.35)',
                      borderRadius: 3,
                      padding: '1px 3px',
                      marginBottom: 1,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      textDecoration: e.completed ? 'line-through' : 'none',
                    }}>
                      {emoji} {e.title}
                    </div>
                  );
                })}
                {inMonth && extra > 0 && (
                  <div style={{fontSize:10,color:'#6b7280',fontWeight:600,paddingLeft:2}}>+{extra} more</div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function navLabel() {
    if (viewMode === 'month') {
      return monthAnchor.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
    }
    return fmtWeekRange(monday);
  }

  function prevNav() {
    if (viewMode === 'month') {
      setMonthAnchor(m => { const d = new Date(m); d.setMonth(d.getMonth()-1); return d; });
    } else {
      setMonday(m => addDays(m, -7));
    }
  }
  function nextNav() {
    if (viewMode === 'month') {
      setMonthAnchor(m => { const d = new Date(m); d.setMonth(d.getMonth()+1); return d; });
    } else {
      setMonday(m => addDays(m, 7));
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={{maxWidth:1000,margin:'0 auto',padding:'1rem 1rem 4rem'}}>
      <h2 style={{fontSize:20,fontWeight:700,color:'#1a2e3b',margin:'0 0 0.25rem'}}>Family Calendar</h2>
      <p style={{fontSize:13,color:'#6b7280',margin:'0 0 1rem'}}>Tap a day to add a task. Ask Aidey "give Sam the bins every Thursday" or "what's on this week".</p>

      {/* Aidey-detected events from emails — shared banner (Family + Business) */}
      <EventProposalsBanner onChange={load} />

      {/* Nav + view toggle */}
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12,flexWrap:'wrap'}}>
        <button onClick={prevNav} style={{padding:'6px 12px',borderRadius:8,border:'1px solid #d1d5db',background:'#fff',cursor:'pointer',fontSize:14}}>← Prev</button>
        <span style={{fontWeight:600,fontSize:13,color:'#374151',flex:1,textAlign:'center',minWidth:160}}>{navLabel()}</span>
        <button onClick={nextNav} style={{padding:'6px 12px',borderRadius:8,border:'1px solid #d1d5db',background:'#fff',cursor:'pointer',fontSize:14}}>Next →</button>
        {viewMode !== 'month' && isoDate(monday) !== isoDate(weekMonday(new Date())) && (
          <button onClick={()=>setMonday(weekMonday(new Date()))} style={{padding:'6px 10px',borderRadius:8,border:'1px solid #3b82f6',background:'#eff6ff',color:'#1d4ed8',cursor:'pointer',fontSize:12,fontWeight:600}}>This week</button>
        )}
        <div style={{display:'flex',borderRadius:8,overflow:'hidden',border:'1px solid #d1d5db'}}>
          {[['week','Week'],['month','Month'],['day','Day']].map(([v,l]) => (
            <button key={v} onClick={()=>{ setViewMode(v); if(v==='month') setMonthAnchor(monthStart(monday)); }} style={{padding:'6px 12px',border:'none',borderRight:v!=='day'?'1px solid #d1d5db':'none',background:viewMode===v?'#1a2e3b':'#fff',color:viewMode===v?'#fff':'#374151',cursor:'pointer',fontSize:13,fontWeight:600}}>{l}</button>
          ))}
        </div>
      </div>

      {/* Category filter chips */}
      <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:8}}>
        {[{value:'',label:'All'},...visibleTaskTypes].map(t => (
          <button key={t.value} onClick={()=>setCatFilter(t.value)}
            style={{padding:'4px 10px',borderRadius:20,border:`1px solid ${catFilter===t.value?'#1a2e3b':'#d1d5db'}`,background:catFilter===t.value?'#1a2e3b':'#fff',color:catFilter===t.value?'#fff':'#374151',fontSize:12,cursor:'pointer',fontWeight:catFilter===t.value?700:400}}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Member filter chips */}
      {members.length > 1 && (
        <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:12}}>
          <button onClick={()=>setMemberFilter('')} style={{padding:'4px 10px',borderRadius:20,border:`1px solid ${memberFilter===''?'#1a2e3b':'#d1d5db'}`,background:memberFilter===''?'#1a2e3b':'#fff',color:memberFilter===''?'#fff':'#374151',fontSize:12,cursor:'pointer'}}>Everyone</button>
          {members.map(m => (
            <button key={m.id} onClick={()=>setMemberFilter(f=>f===m.id?'':m.id)}
              style={{display:'flex',alignItems:'center',gap:5,padding:'4px 10px',borderRadius:20,border:`2px solid ${memberFilter===m.id?memberColour(m.id):'#d1d5db'}`,background:memberFilter===m.id?memberColour(m.id)+'22':'#fff',color:memberFilter===m.id?memberColour(m.id):'#374151',fontSize:12,cursor:'pointer',fontWeight:memberFilter===m.id?700:400}}>
              <span style={{width:10,height:10,borderRadius:'50%',background:memberColour(m.id),display:'inline-block'}}/>
              {m.name}
            </button>
          ))}
        </div>
      )}

      {error && <p style={{color:'#b91c1c',fontSize:13,marginBottom:8}}>{error}</p>}
      {loading && <p style={{color:'#6b7280',fontSize:13}}>Loading…</p>}

      {!loading && viewMode === 'week' && (
        <div style={{display:'flex',gap:4,overflowX:'auto'}}>
          {days.map(day => <DayColumn key={isoDate(day)} day={day} />)}
        </div>
      )}

      {!loading && viewMode === 'day' && <DayView />}

      {!loading && viewMode === 'month' && <MonthView />}

      {modal && <TaskModal />}
    </div>
  );
}
